import { TFilterField, TFilterValue } from "@irohana-dev/components";
import { TOption } from "@/types/common";
import { applyColumnSettings as applyKitColumnSettings, asStringArray, buildColumnOptions, fromDateRangeValue } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import {
  TALENT_MULTI_SELECT_FILTER_KEYS,
  TTalentDisplayColumnSetting,
  TTalentFilterFormExtended,
  defaultTalentDisplayColumns,
  defaultTalentFilterValues,
} from "@/features/talent/components/talent-list/talent-filter-shared";

const DATE_RANGE_KEY = "registrationDate";

type TAsyncFieldSearchConfig = Extract<TFilterField, { type: "asyncMultiSelect" }>["searchConfig"];

type TTalentFilterOptions = {
  recruitment: TOption[];
  talentNumber: TOption[];
  fullName: TOption[];
  nickName: TOption[];
  nationality: TOption[];
  gender: TOption[];
  caseMatchStatus: TOption[];
  residence: TOption[];
  language: TOption[];
  industry: TOption[];
  user: TOption[];
  workLocation: TOption[];
  education: TOption[];
};

export const buildTalentFilterFields = (
  options: TTalentFilterOptions,
  locationOptions: TOption[],
  partnerSearchConfig: TAsyncFieldSearchConfig,
): TFilterField[] => [
  { type: "multiSelect", key: "recruitmentStatus", label: "ステータス", options: options.recruitment, placeholder: "選択してください" },
  { type: "freeText", key: "talentNumber", label: "人材管理番号", options: options.talentNumber, placeholder: "入力してください" },
  { type: "freeText", key: "fullName", label: "氏名", options: options.fullName, placeholder: "入力してください" },
  { type: "freeText", key: "nickName", label: "呼び名", options: options.nickName, placeholder: "入力してください" },
  { type: "multiSelect", key: "nationalityId", label: "国籍", options: options.nationality, placeholder: "選択してください" },
  { type: "multiSelect", key: "gender", label: "性別", options: options.gender, placeholder: "選択してください" },
  { type: "multiSelect", key: "latestProgress", label: "最新進捗", options: options.caseMatchStatus, placeholder: "選択してください" },
  { type: "multiSelect", key: "residenceStatusId", label: "在留資格", options: options.residence, placeholder: "選択してください" },
  { type: "multiSelect", key: "languageTestLevelId", label: "日本語能力", options: options.language, placeholder: "選択してください" },
  { type: "multiSelect", key: "companySpecificIndustryId", label: "分野", options: options.industry, placeholder: "選択してください" },
  { type: "multiSelect", key: "location", label: "居住地", options: locationOptions, placeholder: "選択してください" },
  { type: "multiSelect", key: "personInChargeId", label: "担当者", options: options.user, placeholder: "選択してください" },
  { type: "dateRange", key: DATE_RANGE_KEY, label: "登録日" },
  { type: "freeText", key: "desiredWorkLocation", label: "希望勤務地", options: options.workLocation, placeholder: "入力してください" },
  { type: "text", key: "heldQualifications", label: "資格", placeholder: "入力してください" },
  { type: "multiSelect", key: "educationLevelId", label: "学歴", options: options.education, placeholder: "選択してください" },
  { type: "checkbox", key: "isRecommendedTalent", label: "おすすめ人材" },
  { type: "asyncMultiSelect", key: "partnerId", label: "パートナー名", searchConfig: partnerSearchConfig, placeholder: "選択してください" },
];

export const buildTalentColumnOptions = buildColumnOptions;

export const toFilterValue = (filters: TTalentFilterFormExtended): TFilterValue => {
  const value: TFilterValue = {};
  for (const [key, raw] of Object.entries(filters)) {
    if (key === "registrationDateFrom" || key === "registrationDateTo") continue;
    if (key === "isRecommendedTalent") {
      if (raw === "1") value[key] = true;
      continue;
    }
    if (Array.isArray(raw)) {
      if (raw.length > 0) value[key] = raw.map(String);
      continue;
    }
    if (raw === "" || raw === null || raw === undefined) continue;
    if (TALENT_MULTI_SELECT_FILTER_KEYS.includes(key as keyof TTalentFilterFormExtended)) {
      value[key] = [String(raw)];
      continue;
    }
    value[key] = String(raw);
  }
  const { registrationDateFrom, registrationDateTo } = filters;
  if (registrationDateFrom || registrationDateTo) {
    value[DATE_RANGE_KEY] = { from: registrationDateFrom, to: registrationDateTo };
  }
  return value;
};

export const fromFilterValue = (value: TFilterValue): TTalentFilterFormExtended => {
  const range = fromDateRangeValue(value, DATE_RANGE_KEY);
  return {
    ...defaultTalentFilterValues,
    recruitmentStatus: asStringArray(value.recruitmentStatus),
    talentNumber: (value.talentNumber as string) ?? "",
    fullName: (value.fullName as string) ?? "",
    nickName: (value.nickName as string) ?? "",
    nationalityId: asStringArray(value.nationalityId),
    gender: asStringArray(value.gender),
    latestProgress: asStringArray(value.latestProgress),
    residenceStatusId: asStringArray(value.residenceStatusId),
    languageTestLevelId: asStringArray(value.languageTestLevelId),
    companySpecificIndustryId: asStringArray(value.companySpecificIndustryId),
    location: asStringArray(value.location),
    personInChargeId: asStringArray(value.personInChargeId),
    desiredWorkLocation: (value.desiredWorkLocation as string) ?? "",
    heldQualifications: ((value.heldQualifications as string) ?? "").trim(),
    educationLevelId: asStringArray(value.educationLevelId),
    isRecommendedTalent: value.isRecommendedTalent === true ? "1" : "",
    partnerId: asStringArray(value.partnerId),
    registrationDateFrom: range.from,
    registrationDateTo: range.to,
  };
};

export const applyColumnSettings = (columns: TTalentDisplayColumnSetting[], order: string[], hidden: string[]): TTalentDisplayColumnSetting[] =>
  applyKitColumnSettings(columns, order, hidden, defaultTalentDisplayColumns);
